/**
 * Sidebar navigation (256px on `lg:`, drawer on mobile) per STANDARDSv2 §Layout.
 *
 * Admin-gated items are **hidden entirely** rather than disabled — notably "Admin guide", since
 * ADMIN_GUIDE.md carries operational detail (infra config, backup procedures, security
 * hardening) and treating it as public is a security leak (CLAUDE.md §In-App Help).
 */

import { Dialog, Transition } from '@headlessui/react';
import {
  ArrowPathIcon,
  ArrowTrendingUpIcon,
  CubeIcon,
  RectangleStackIcon,
  BanknotesIcon,
  FunnelIcon,
  BoltIcon,
  BookOpenIcon,
  BuildingOffice2Icon,
  CheckCircleIcon,
  ClockIcon,
  CloudIcon,
  Cog6ToothIcon,
  HomeIcon,
  LinkIcon,
  RectangleGroupIcon,
  ShieldCheckIcon,
  TicketIcon,
  UsersIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { Fragment, type ComponentType, type SVGProps } from 'react';
import { NavLink } from 'react-router-dom';

import { useAuth } from '@/contexts/AuthContext';

interface NavItem {
  label: string;
  to: string;
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  /** Unprefixed permission key; omitted = visible to any authenticated user. */
  permission?: string;
}

const MAIN_NAV: NavItem[] = [
  { label: 'Dashboard', to: '/dashboard', icon: HomeIcon },
  { label: 'Tickets', to: '/tickets', icon: TicketIcon, permission: 'tickets.view' },
  {
    label: 'Companies',
    to: '/companies',
    icon: BuildingOffice2Icon,
    permission: 'crm.companies.view',
  },
  { label: 'Contacts', to: '/contacts', icon: UsersIcon, permission: 'crm.contacts.view' },
  // Day-to-day work, so main nav rather than the admin section: templates are the only part of
  // projects an administrator configures, and they live inside the same screen.
  { label: 'Projects', to: '/projects', icon: RectangleStackIcon, permission: 'projects.view' },
  { label: 'Assets', to: '/assets', icon: CubeIcon, permission: 'assets.view' },
  { label: 'Knowledge base', to: '/kb', icon: BookOpenIcon, permission: 'kb.view' },
  {
    label: 'Device sources',
    to: '/admin/device-sources',
    icon: CubeIcon,
    permission: 'assets.manage',
  },
  {
    label: 'External data',
    to: '/external-data',
    icon: CloudIcon,
    permission: 'integrations.mirror.view',
  },
];

const DOCS_NAV: NavItem[] = [
  { label: 'User guide', to: '/docs/user-guide', icon: BookOpenIcon },
  {
    label: 'My timesheet',
    to: '/time/timesheet',
    icon: ClockIcon,
    permission: 'time.view',
  },
  {
    label: 'Time approvals',
    to: '/time/approvals',
    icon: CheckCircleIcon,
    permission: 'time.approve',
  },
];

const ADMIN_NAV: NavItem[] = [
  {
    label: 'Bedrock Registration',
    to: '/admin/bedrock-registration',
    icon: ShieldCheckIcon,
    permission: 'admin.users.manage',
  },
  {
    label: 'Company matching',
    to: '/admin/company-matching',
    icon: LinkIcon,
    permission: 'admin.company-matching.manage',
  },
  {
    label: 'Custom fields',
    to: '/admin/custom-fields',
    icon: RectangleGroupIcon,
    permission: 'admin.custom-fields.manage',
  },
  {
    label: 'SLA policies',
    to: '/admin/sla',
    icon: ClockIcon,
    permission: 'sla.manage',
  },
  {
    label: 'Device sources',
    to: '/admin/device-sources',
    icon: CubeIcon,
    permission: 'assets.manage',
  },
  {
    label: 'External data',
    to: '/admin/external-data',
    icon: ArrowPathIcon,
    permission: 'integrations.mirror.sync',
  },
  {
    label: 'Classification',
    to: '/admin/classification',
    icon: FunnelIcon,
    permission: 'admin.classification.manage',
  },
  {
    label: 'Contracts',
    to: '/admin/contracts',
    icon: BanknotesIcon,
    permission: 'time.contracts.manage',
  },
  {
    label: 'Escalation paths',
    to: '/admin/escalation-paths',
    icon: ArrowTrendingUpIcon,
    permission: 'sla.escalation.manage',
  },
  {
    label: 'Workflows',
    to: '/admin/workflows',
    icon: BoltIcon,
    permission: 'sla.workflows.manage',
  },
  { label: 'Settings', to: '/admin/settings', icon: Cog6ToothIcon, permission: 'admin.settings' },
];

/** Admin-only: never rendered for non-admins (not merely disabled). */
const ADMIN_DOCS_NAV: NavItem[] = [
  { label: 'Admin guide', to: '/docs/admin-guide', icon: BookOpenIcon },
];

function navClasses(isActive: boolean): string {
  return [
    'flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors border-l-2',
    isActive
      ? 'bg-primary-50 text-primary-700 border-primary-500 dark:bg-primary-500/10 dark:text-primary-300'
      : 'border-transparent text-surface-600 hover:bg-surface-100 dark:text-surface-400 dark:hover:bg-surface-800',
  ].join(' ');
}

function NavItems({ items, onNavigate }: { items: NavItem[]; onNavigate?: () => void }) {
  const { hasPermission } = useAuth();

  return (
    <>
      {items
        .filter((item) => !item.permission || hasPermission(item.permission))
        .map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            onClick={onNavigate}
            className={({ isActive }) => navClasses(isActive)}
          >
            <item.icon className="h-5 w-5 shrink-0" aria-hidden="true" />
            <span className="truncate">{item.label}</span>
          </NavLink>
        ))}
    </>
  );
}

function SectionHeading({ children }: { children: string }) {
  return (
    <p className="px-3 pt-4 pb-1 text-xs font-semibold uppercase tracking-wider text-surface-500 dark:text-surface-400">
      {children}
    </p>
  );
}

function SidebarContent({ onNavigate }: { onNavigate?: () => void }) {
  const { isAdmin } = useAuth();

  const adminItems = [...ADMIN_NAV, ...(isAdmin ? ADMIN_DOCS_NAV : [])];

  return (
    <nav className="flex flex-col gap-1 p-3" aria-label="Main navigation">
      <NavItems items={MAIN_NAV} onNavigate={onNavigate} />

      <SectionHeading>Help</SectionHeading>
      <NavItems items={DOCS_NAV} onNavigate={onNavigate} />

      {isAdmin ? (
        <>
          <div className="mt-2 border-t border-surface-200 dark:border-surface-700" />
          <SectionHeading>Administration</SectionHeading>
          <NavItems items={adminItems} onNavigate={onNavigate} />
        </>
      ) : null}
    </nav>
  );
}

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

export function Sidebar({ isOpen, onClose }: SidebarProps) {
  return (
    <>
      {/* Desktop: fixed 256px column. */}
      <aside className="hidden lg:block w-64 shrink-0 border-r border-surface-200 dark:border-surface-700 bg-white dark:bg-surface-900 overflow-y-auto">
        <SidebarContent />
      </aside>

      {/* Mobile: modal drawer. */}
      <Transition show={isOpen} as={Fragment}>
        <Dialog onClose={onClose} className="relative z-50 lg:hidden">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-surface-950/40" aria-hidden="true" />
          </Transition.Child>

          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="-translate-x-full"
            enterTo="translate-x-0"
            leave="ease-in duration-200"
            leaveFrom="translate-x-0"
            leaveTo="-translate-x-full"
          >
            <Dialog.Panel className="fixed inset-y-0 left-0 w-64 bg-white dark:bg-surface-900 shadow-modal overflow-y-auto">
              <div className="flex items-center justify-between h-16 px-4 border-b border-surface-200 dark:border-surface-700">
                <Dialog.Title className="text-base font-semibold text-surface-900 dark:text-surface-100">
                  Menu
                </Dialog.Title>
                <button
                  type="button"
                  onClick={onClose}
                  aria-label="Close navigation"
                  className="p-2 rounded-lg text-surface-500 hover:bg-surface-100 dark:hover:bg-surface-800"
                >
                  <XMarkIcon className="h-5 w-5" aria-hidden="true" />
                </button>
              </div>
              <SidebarContent onNavigate={onClose} />
            </Dialog.Panel>
          </Transition.Child>
        </Dialog>
      </Transition>
    </>
  );
}
